/**
 * /library/riwayat — progress-led worksheet history.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect, useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Colors, Radii, Spacing } from '@/constants/theme';
import { getActiveChild } from '@/lib/api/children';
import { getHistory } from '@/lib/api/history';
import { printBatch, summarizeBatch, type BatchPrintItem } from '@/lib/print';
import { ApiError } from '@/lib/api/client';
import { ONBOARDING_ROUTES } from '@/constants/onboarding';

import { HistoryList } from '@/components/library/HistoryList';
import { LibraryEmptyState } from '@/components/library/LibraryEmptyState';
import { ConfirmationModal } from '@/components/library/ConfirmationModal';
import {
  PrintStatusCard,
  WorksheetDistributionCard,
} from '@/components/library/ProgressRecap';
import { Toast } from '@/components/library/Toast';
import { MainPageHeader } from '@/components/main/MainPageHeader';
import { isPendingPrint } from '@/lib/history-presenter';

import type { ChildProfile } from '@funpaper/shared';
import type {
  HistoryItem,
  HistoryPage,
  HistoryRange,
} from '@/types/history';

type HistorySummary = Pick<HistoryPage, 'totalCount' | 'activityCounts' | 'statusCounts'>;

type LibraryState =
  | { kind: 'loading' }
  | { kind: 'ready'; child: ChildProfile; history: HistoryPage; allTimeSummary: HistorySummary }
  | { kind: 'empty'; child: ChildProfile | null; reason: 'no_data' | 'new_user' }
  | { kind: 'error'; message: string };

const PAGE_SIZE = 20;
const MAX_BATCH_PRINT = 5;

export default function RiwayatScreen() {
  const router = useRouter();
  const [state, setState] = useState<LibraryState>({ kind: 'loading' });
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyRange, setHistoryRange] = useState<HistoryRange>('7d');
  const [toast, setToast] = useState<string | null>(null);
  const firstFocusRef = useRef(true);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Batch print modal
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchInProgress, setBatchInProgress] = useState(false);
  const batchInProgressRef = useRef(false);

  const showToast = useCallback((message: string, durationMs = 1800) => {
    setToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, durationMs);
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // --------------------------------------------------------------------------
  // Initial load + pull-to-refresh
  // --------------------------------------------------------------------------

  const loadData = useCallback(async (mode: 'initial' | 'refresh', range: HistoryRange) => {
    let child: ChildProfile | null = null;
    try {
      child = await getActiveChild();
    } catch {
      if (mode === 'initial') {
        router.replace(ONBOARDING_ROUTES.story as never);
      }
      return;
    }
    if (!child) {
      setState({ kind: 'empty', child: null, reason: 'new_user' });
      return;
    }

    try {
      const allTimeHistoryPromise = range === 'all'
        ? Promise.resolve(null)
        : getHistory(child.id, 1, 1, 'all');
      const [history, allTimeHistory] = await Promise.all([
        getHistory(child.id, 1, PAGE_SIZE, range),
        allTimeHistoryPromise,
      ]);
      const summary = allTimeHistory ?? history;
      setHistoryPage(1);
      if (history.items.length === 0 && range === 'all') {
        setState({ kind: 'empty', child, reason: 'no_data' });
      } else {
        setState({
          kind: 'ready',
          child,
          history,
          allTimeSummary: {
            totalCount: summary.totalCount,
            activityCounts: summary.activityCounts,
            statusCounts: summary.statusCounts,
          },
        });
      }
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'Gagal memuat aktivitas. Coba lagi.';
      setState({ kind: 'error', message });
    }
  }, [router]);

  useFocusEffect(
    useCallback(() => {
      const initial = firstFocusRef.current;
      firstFocusRef.current = false;
      if (!initial) setRefreshing(true);

      void loadData(initial ? 'initial' : 'refresh', historyRange).finally(() => {
        if (!initial) setRefreshing(false);
      });
    }, [historyRange, loadData]),
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadData('refresh', historyRange);
    } finally {
      setRefreshing(false);
    }
  }, [loadData, historyRange]);

  const onRangeChange = useCallback((range: HistoryRange) => {
    if (range === historyRange) return;
    setHistoryRange(range);
  }, [historyRange]);

  const pendingItems = state.kind === 'ready'
    ? state.history.items.filter(isPendingPrint)
    : [];
  const batchItems = pendingItems.slice(0, MAX_BATCH_PRINT);
  const todayIso = new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // --------------------------------------------------------------------------
  // Infinite scroll
  // --------------------------------------------------------------------------

  const onEndReached = useCallback(async () => {
    if (state.kind !== 'ready') return;
    if (loadingMore || loadingMoreRef.current || !state.history.hasMore) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const next = await getHistory(state.child.id, historyPage + 1, PAGE_SIZE, historyRange);
      setState({
        ...state,
        history: {
          ...next,
          items: [...state.history.items, ...next.items],
        },
      });
      setHistoryPage((p) => p + 1);
    } catch {
      // Silent fail on pagination — keep existing items.
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [state, loadingMore, historyPage, historyRange]);

  // --------------------------------------------------------------------------
  // Card press → Detail
  // --------------------------------------------------------------------------

  const onItemPress = useCallback(
    (item: HistoryItem) => {
      router.push(`/library/worksheet/${item.worksheetId}` as never);
    },
    [router],
  );

  const onPrintOne = useCallback(async (item: HistoryItem) => {
    if (state.kind !== 'ready') return;
    try {
      const result = await printBatch([{
        id: item.worksheetId,
        name: item.name,
        pdfUrl: item.pdfUrl,
      }], 'print', state.child.id);
      showToast(summarizeBatch('print', result));
      await loadData('refresh', historyRange);
    } catch {
      showToast('Cetak worksheet gagal');
    }
  }, [state, showToast, loadData, historyRange]);

  // --------------------------------------------------------------------------
  // Batch print
  // --------------------------------------------------------------------------

  const onCetakSemuaPress = useCallback(() => {
    if (batchItems.length === 0) return;
    setShowBatchModal(true);
  }, [batchItems]);

  const onCetakSemuaConfirm = useCallback(async () => {
    if (state.kind !== 'ready' || batchInProgressRef.current) return;
    batchInProgressRef.current = true;
    setBatchInProgress(true);
    setShowBatchModal(false);
    const items: BatchPrintItem[] = batchItems
      .map((item) => ({
        id: item.worksheetId,
        name: item.name,
        pdfUrl: item.pdfUrl,
      }));
    showToast(`Mencetak ${items.length} worksheet satu per satu…`);
    try {
      const result = await printBatch(items, 'print', state.child.id);
      showToast(summarizeBatch('print', result));
      await loadData('refresh', historyRange);
    } catch {
      showToast('Cetak batch gagal');
    } finally {
      batchInProgressRef.current = false;
      setBatchInProgress(false);
    }
  }, [state, batchItems, showToast, loadData, historyRange]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        {state.kind === 'loading' ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={Brand.primary} />
          </View>
        ) : state.kind === 'error' ? (
          <>
            <RiwayatHeader childName="" />
            <LibraryEmptyState
              variant="error"
              errorMessage={state.message}
              onRetry={() => void loadData('initial', historyRange)}
            />
          </>
        ) : state.kind === 'empty' ? (
          <>
            <RiwayatHeader childName={state.child?.name ?? ''} />
            <LibraryEmptyState
              variant={state.reason}
              childName={state.child?.name}
            />
          </>
        ) : (
          <HistoryList
            header={
              <>
                <View style={styles.pageHeaderSection}>
                  <RiwayatHeader childName={state.child.name} />
                  <View style={styles.recapCards}>
                    <PrintStatusCard
                      totalSelected={state.allTimeSummary.totalCount}
                      printedCount={state.allTimeSummary.statusCounts.printed}
                      needsPrintCount={state.allTimeSummary.statusCounts.needsPrint}
                      periodLabel="Semua aktivitas"
                    />
                    <WorksheetDistributionCard
                      totalSelected={state.allTimeSummary.totalCount}
                      activityCounts={state.allTimeSummary.activityCounts}
                    />
                  </View>
                  <View style={styles.filterRow}>
                    <HistoryFilter value={historyRange} onChange={onRangeChange} />
                    {batchItems.length > 0 && !batchInProgress ? (
                      <Pressable
                        onPress={onCetakSemuaPress}
                        style={({ pressed }) => [styles.batchBtn, pressed && styles.batchBtnPressed]}
                        accessibilityRole="button"
                        accessibilityLabel={`Cetak ${batchItems.length} worksheet yang belum dicetak`}
                      >
                        <Text style={styles.batchBtnText}>Cetak {batchItems.length} yang belum</Text>
                      </Pressable>
                    ) : null}
                  </View>
                </View>
                <View style={styles.listSectionHeader}>
                  <ThemedText type="smallBold" style={styles.sectionLabel}>
                    Pilihan terbaru
                  </ThemedText>
                  <ThemedText type="small" themeColor="textSecondary">
                    {state.history.totalCount} worksheet
                  </ThemedText>
                </View>
              </>
            }
            emptyComponent={
              <LibraryEmptyState variant="filtered" childName={state.child.name} />
            }
            items={state.history.items}
            hasMore={state.history.hasMore}
            loading={loadingMore}
            onEndReached={onEndReached}
            onItemPress={onItemPress}
            todayIso={todayIso}
            onPrint={onPrintOne}
            onRefresh={onRefresh}
            refreshing={refreshing}
          />
        )}
        <Toast message={toast} />

        {/* Modals */}
        <ConfirmationModal
          visible={showBatchModal}
          title="Cetak yang belum dicetak"
          body={`Cetak ${batchItems.length} worksheet yang belum dicetak? Dialog browser/printer akan muncul satu per satu.`}
          confirmLabel="Cetak"
          cancelLabel="Batal"
          onConfirm={() => void onCetakSemuaConfirm()}
          onCancel={() => setShowBatchModal(false)}
          confirmDisabled={batchInProgress}
        />
      </SafeAreaView>
    </ThemedView>
  );
}

// ----------------------------------------------------------------------------
// RiwayatHeader
// ----------------------------------------------------------------------------

function RiwayatHeader({ childName }: { childName: string }) {
  return (
    <MainPageHeader
      title={childName ? `Perkembangan ${childName}` : 'Perkembangan'}
      subtitle="Aktivitas worksheet"
      style={styles.historyHeader}
    />
  );
}

const HISTORY_FILTERS: { value: HistoryRange; label: string }[] = [
  { value: '7d', label: '7 hari terakhir' },
  { value: 'all', label: 'Semua waktu' },
];

function HistoryFilter({
  value,
  onChange,
}: {
  value: HistoryRange;
  onChange: (range: HistoryRange) => void;
}) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.filterContent}
      style={styles.filterScroll}
    >
      {HISTORY_FILTERS.map((filter) => {
        const selected = filter.value === value;
        return (
          <Pressable
            key={filter.value}
            onPress={() => onChange(filter.value)}
            style={({ pressed }) => [
              styles.filterChip,
              selected && styles.filterChipSelected,
              pressed && styles.filterChipPressed,
            ]}
            accessibilityRole="button"
            accessibilityLabel={`Filter ${filter.label}`}
            accessibilityState={{ selected }}
          >
            <Text style={[styles.filterText, selected && styles.filterTextSelected]}>
              {filter.label}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  historyHeader: { paddingHorizontal: Spacing.four },
  pageHeaderSection: {
    borderBottomColor: Colors.light.border,
    borderBottomWidth: 1,
    gap: Spacing.three,
    paddingBottom: Spacing.two,
  },
  recapCards: {
    gap: Spacing.two,
    paddingHorizontal: Spacing.four,
  },
  filterRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: Spacing.two,
    paddingHorizontal: Spacing.four,
  },
  listSectionHeader: {
    alignItems: 'baseline',
    borderBottomColor: Colors.light.border,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.three,
  },
  sectionLabel: { color: Colors.light.text, fontSize: 14, fontWeight: '700' },
  batchBtn: {
    backgroundColor: Brand.teal,
    borderRadius: Radii.small,
    paddingHorizontal: Spacing.two,
    paddingVertical: Spacing.two,
  },
  batchBtnPressed: {
    opacity: 0.7,
  },
  batchBtnText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  filterScroll: {
    flex: 1,
    minWidth: 0,
  },
  filterContent: {
    gap: Spacing.two,
  },
  filterChip: {
    backgroundColor: Colors.light.card,
    borderColor: Colors.light.border,
    borderRadius: Radii.pill,
    borderWidth: 1,
    paddingHorizontal: Spacing.two,
    paddingVertical: 7,
  },
  filterChipSelected: {
    backgroundColor: Brand.primary,
    borderColor: Brand.primary,
  },
  filterChipPressed: {
    opacity: 0.75,
  },
  filterText: {
    color: Colors.light.textSecondary,
    fontSize: 12,
    fontWeight: '600',
  },
  filterTextSelected: {
    color: '#ffffff',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
